// src/RedactionWorkbench.js
import { forwardRef, useCallback as useCallback4, useEffect as useEffect7, useImperativeHandle, useMemo as useMemo2, useRef as useRef8, useState as useState9 } from "react";
import { useTheme as useTheme3 } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon2 from "@mui/icons-material/Close";
import FindReplaceOutlinedIcon2 from "@mui/icons-material/FindReplaceOutlined";
import RemoveCircleOutlineIcon2 from "@mui/icons-material/RemoveCircleOutline";
import CircularProgress2 from "@mui/material/CircularProgress";

// src/WorkbenchTheme.js
import { useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { jsx } from "react/jsx-runtime";
function WorkbenchTheme({ tokens, children }) {
  const theme = useMemo(() => createTheme({
    palette: {
      mode: "light",
      primary: { main: tokens.colors.primary, light: tokens.colors.primaryLight },
      error: { main: tokens.colors.error },
      text: { primary: tokens.colors.text.body },
      background: { default: tokens.colors.background.white }
    },
    typography: { fontFamily: tokens.fonts.body },
    shape: { borderRadius: 5 },
    tokens
  }), [tokens]);
  return /* @__PURE__ */ jsx(ThemeProvider, { theme, children });
}

// src/chrome.js
import styled from "@emotion/styled";
import MuiDialog from "@mui/material/Dialog";
var T = (p) => p.theme.tokens;
var C = (pick) => (p) => pick(T(p).colors);
var F = (p) => T(p).fonts.body;
var R = (pick) => (p) => pick(T(p).borderRadius);
var PANEL_WIDTH = 360;
var HEADER_HEIGHT = 64;
var TOOLBAR_HEIGHT = 48;
var aiFill = (colors) => `${colors.text.body}33`;
var aiStroke = (colors) => colors.text.body;
var manualFill = (colors) => `${colors.primary}3A`;
var Shell = styled.div`
  display: flex; flex-direction: column; width: 100%; height: 100vh; min-height: 0; min-width: 0; contain: inline-size; overflow: hidden;
  background: ${C((c) => c.background.light)}; color: ${C((c) => c.text.body)}; font: 400 14px/1.4 ${F};
  & .MuiSvgIcon-root { font-size: 16px; }
`;
var HeaderBar = styled.header`
  display: flex; align-items: center; gap: 14px; min-height: ${HEADER_HEIGHT}px; box-sizing: border-box; padding: 12px 30px; flex: 0 0 auto;
  background: ${C((c) => c.background.white)}; border-bottom: 1px solid ${C((c) => c.sectionDivider)};
`;
var TitleBlock = styled.div`display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1 1 auto;`;
var TitleLine = styled.div`display: flex; align-items: center; gap: 10px; min-width: 0;`;
var Title = styled.h1`margin: 0; font: 600 20px/26px ${F}; color: ${C((c) => c.text.body)}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
var Meta = styled.p`margin: 0; font: 400 13px/17px ${F}; color: ${C((c) => c.text.muted)}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
var HeaderActions = styled.div`display: flex; align-items: center; gap: 8px; flex: 0 0 auto;`;
var UnsavedHint = styled.span`font: 400 12px/16px ${F}; color: ${(p) => p.$error ? T(p).colors.error : T(p).colors.text.muted}; white-space: nowrap; padding: 3px 8px; border-radius: 12px; background: ${C((c) => c.background.light)};`;
var ViewOnlyPill = styled.span`
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 16px; white-space: nowrap;
  border: 1px solid ${C((c) => c.sectionDivider)}; background: ${C((c) => c.background.light)}; font: 400 13px/16px ${F};
  & .MuiSvgIcon-root { font-size: 14px; }
`;
var buttonBase = (p) => `
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; box-sizing: border-box; cursor: pointer;
  padding: 8px 20px; border-radius: ${T(p).borderRadius.button}; font: 500 14px/19px ${F(p)}; white-space: nowrap;
  &:disabled { cursor: default; opacity: 0.5; }
  &:focus:not(:focus-visible) { outline: none; }
`;
var PrimaryButton = styled.button`
  ${buttonBase} background: ${C((c) => c.primary)}; color: ${C((c) => c.white)}; border: 1px solid ${C((c) => c.primary)};
  &:hover:not(:disabled) { opacity: 0.9; }
`;
var SecondaryButton = styled.button`
  ${buttonBase} background: ${C((c) => c.secondaryButton.background)}; color: ${C((c) => c.secondaryButton.text)};
  border: 1px solid ${C((c) => c.secondaryButton.border)};
  &:hover:not(:disabled) { background: ${C((c) => c.secondaryButton.hover)}; }
`;
var DangerOutlineButton = styled(SecondaryButton)`
  color: ${C((c) => c.error)}; border-color: ${C((c) => c.error)};
  &:hover:not(:disabled) { background: ${C((c) => c.errorLight)}; }
`;
var TextButton = styled.button`
  background: transparent; border: none; padding: 8px 6px; cursor: pointer; font: 500 14px/19px ${F}; white-space: nowrap;
  color: ${(p) => p.$danger ? T(p).colors.error : T(p).colors.text.link};
  &:hover:not(:disabled) { opacity: 0.8; } &:disabled { cursor: default; opacity: 0.5; }
`;
var IconOnlyButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; padding: 0; cursor: pointer;
  border: none; border-radius: ${R((r) => r.iconButton)}; background: transparent; color: ${C((c) => c.text.body)};
  &:hover:not(:disabled) { background: ${C((c) => c.background.medium)}; } &:disabled { cursor: default; opacity: 0.5; }
  &:focus:not(:focus-visible) { outline: none; }
  & .MuiSvgIcon-root { font-size: 18px; }
`;
var Toolbar = styled.div`
  display: flex; align-items: center; gap: 10px; min-height: ${TOOLBAR_HEIGHT}px; box-sizing: border-box; padding: 10px 30px; flex: 0 0 auto; flex-wrap: wrap;
  background: ${C((c) => c.background.white)}; border-bottom: 1px solid ${(p) => p.$withNote ? "transparent" : T(p).colors.sectionDivider};
`;
var ToolbarLayout = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; gap: 16px; width: 100%;
  @media (max-width: 980px) { grid-template-columns: 1fr auto; & > div:nth-child(2) { justify-content: flex-start; } & > div:nth-child(3) { grid-column: 1 / -1; justify-content: flex-start; } }
`;
var ToolbarLeft = styled.div`display: flex; align-items: center; justify-content: flex-start; gap: 8px; min-width: 0;`;
var ToolbarCenter = styled.div`display: flex; align-items: center; justify-content: center;`;
var ToolbarRight = styled.div`display: flex; align-items: center; justify-content: flex-end; gap: 8px; min-width: 0;`;
var ToolButton = styled.button`
  display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; cursor: pointer; font: 500 13px/16px ${F}; white-space: nowrap;
  border-radius: 20px; background: ${C((c) => c.background.white)}; color: ${C((c) => c.text.body)}; border: 1px solid ${C((c) => c.secondaryButton.border)};
  &:hover:not(:disabled) { background: ${C((c) => c.secondaryButton.hover)}; } &:disabled { cursor: default; opacity: 0.5; }
  &:focus:not(:focus-visible) { outline: none; }
`;
var SquareIconButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; padding: 0; cursor: pointer;
  border-radius: ${R((r) => r.iconButton)}; background: ${(p) => p.$active ? T(p).colors.primaryLight : T(p).colors.background.white};
  border: 1px solid ${(p) => p.$active ? T(p).colors.primary : T(p).colors.sectionDivider}; color: ${(p) => p.$active ? T(p).colors.primary : T(p).colors.text.body};
  &:hover:not(:disabled) { background: ${(p) => p.$active ? T(p).colors.primaryLight : T(p).colors.secondaryButton.hover}; } &:disabled { cursor: default; opacity: 0.5; }
  &:focus:not(:focus-visible) { outline: none; }
  & .MuiSvgIcon-root { font-size: 16px; }
`;
var ToolGroup = styled.div`display: flex; align-items: center; gap: 6px;`;
var Sep = styled.span`width: 1px; height: 24px; background: ${C((c) => c.sectionDivider)};`;
var ToolText = styled.span`font: 400 13px/16px ${F}; color: ${C((c) => c.text.body)}; white-space: nowrap;`;
var ZoomLabel = styled(ToolText)`min-width: 37px; text-align: center;`;
var ToolInput = styled.input`
  box-sizing: border-box; height: 26px; padding: 0 8px; font: 400 13px ${F}; color: ${C((c) => c.text.body)}; text-align: center;
  background: ${C((c) => c.background.white)}; border: 1px solid ${C((c) => c.sectionDivider)}; border-radius: ${R((r) => r.input)};
  &:focus { outline: none; border-color: ${C((c) => c.primary)}; }
`;
var PageInput = styled(ToolInput)`width: 48px;`;
var SearchInput = styled(ToolInput)`width: 180px; text-align: left;`;
var Spacer = styled.span`flex: 1;`;
var StaleBanner = styled.div`
  display: flex; align-items: center; gap: 12px; height: 52px; box-sizing: border-box; padding: 10px 20px; flex: 0 0 auto;
  background: ${C((c) => `${c.status.warning}1A`)}; border-bottom: 1px solid ${C((c) => c.status.warning)}; font: 400 14px/18px ${F};
  & > .MuiSvgIcon-root { font-size: 18px; color: ${C((c) => c.status.warning)}; flex: 0 0 auto; }
`;
var BannerText = styled.span`flex: 1 1 auto; min-width: 0;`;
var ErrorLine = styled.p`margin: 0; padding: 6px 20px; font: 400 12px/15px ${F}; color: ${C((c) => c.error)};`;
var InfoBand = styled.div`
  display: flex; align-items: center; gap: 12px; min-height: 44px; box-sizing: border-box; padding: 8px 20px; flex: 0 0 auto;
  background: ${(p) => p.$tone === "warning" ? `${T(p).colors.status.warning}1A` : T(p).colors.primaryExtraLight};
  border-bottom: 1px solid ${(p) => p.$tone === "warning" ? T(p).colors.status.warning : T(p).colors.primary}; font: 400 14px/18px ${F}; color: ${C((c) => c.text.body)};
  & > .MuiSvgIcon-root { font-size: 18px; color: ${(p) => p.$tone === "warning" ? T(p).colors.status.warning : T(p).colors.primary}; flex: 0 0 auto; }
`;
var BandLead = styled.span`display: block;`;
var BandDetail = styled.span`display: block; margin-top: 2px; font: 400 12px/15px ${F}; color: ${C((c) => c.text.muted)};`;
var ToolNoteRow = styled.div`display: flex; justify-content: flex-end; padding: 0 20px 6px; margin-top: -1px; flex: 0 0 auto; background: ${C((c) => c.background.light)}; border-bottom: 1px solid ${C((c) => c.sectionDivider)};`;
var ToolHint = styled.span`display: inline-flex; align-items: center; gap: 5px; font: 400 12px/15px ${F}; color: ${C((c) => c.text.muted)}; white-space: nowrap; & .MuiSvgIcon-root { font-size: 14px; }`;
var Body = styled.div`display: flex; flex: 1 1 auto; min-height: 0;`;
var CanvasScroll = styled.div`
  flex: 1 1 auto; min-width: 0; overflow: auto; padding: 30px; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; gap: 14px;
  background: ${C((c) => c.background.medium)};
`;
var PageNumber = styled.span`font: 400 12px/15px ${F}; color: ${C((c) => c.text.muted)};`;
var Legend = styled.div`display: flex; align-items: center; gap: 18px; font: 400 12px/15px ${F}; color: ${C((c) => c.text.muted)}; & > span { display: inline-flex; align-items: center; gap: 6px; }`;
var Swatch = styled.span`
  display: inline-block; width: 14px; height: 14px; box-sizing: border-box; border-radius: 2px; flex: 0 0 auto;
  background: ${(p) => p.$fill}; border: ${(p) => p.$selected ? 2 : 1}px solid ${(p) => p.$stroke};
`;
var RailCount = styled.span`font: 400 12px/15px ${F}; color: ${C((c) => c.text.muted)}; white-space: nowrap;`;
var PageGroup = styled.div`display: flex; justify-content: space-between; padding: 8px 8px 4px; font: 600 12px/15px ${F}; color: ${C((c) => c.text.muted)}; text-transform: uppercase; & > span:last-of-type { font-weight: 400; }`;
var BoxRow = styled.button`
  display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 10px; border: none; border-radius: 8px; cursor: pointer; text-align: left;
  font: 400 13px/18px ${F};
  background: ${(p) => p.$selected ? T(p).colors.primaryExtraLight : "transparent"};
  &:hover { background: ${(p) => p.$selected ? T(p).colors.primaryExtraLight : T(p).colors.background.light}; }
  &:focus:not(:focus-visible) { outline: none; }
`;
var RailEmpty = styled.p`margin: 0; padding: 12px 8px; font: 400 12px/15px ${F}; color: ${C((c) => c.text.muted)};`;
var Panel = styled.aside`
  display: flex; flex-direction: column; gap: 14px; width: ${PANEL_WIDTH}px; flex: 0 0 ${PANEL_WIDTH}px; box-sizing: border-box; padding: 24px 20px 84px; min-height: 0; overflow: auto;
  background: ${C((c) => c.background.white)}; border-left: 1px solid ${C((c) => c.sectionDivider)};
`;
var PanelSection = styled.div`display: flex; flex-direction: column; gap: 14px; min-width: 0;`;
var PanelTitleRow = styled.div`display: flex; align-items: baseline; justify-content: space-between; gap: 12px;`;
var PanelTitle = styled.h2`margin: 0; font: 600 18px/23px ${F}; color: ${C((c) => c.text.body)};`;
var KeyValueRow = styled.div`display: flex; justify-content: space-between; gap: 12px; font: 400 13px/17px ${F}; & > span:first-of-type { color: ${C((c) => c.text.muted)}; } & > span:last-of-type { text-align: right; }`;
var FieldLabel = styled.label`font: 500 14px/17px ${F}; color: ${C((c) => c.text.body)};`;
var ReasonTextarea = styled.textarea`
  width: 100%; min-height: 96px; resize: vertical; box-sizing: border-box; padding: 10px 12px; font: 400 14px/18px ${F}; color: ${C((c) => c.text.body)};
  background: ${C((c) => c.background.medium)}; border: 1px solid ${C((c) => c.sectionDivider)}; border-radius: ${R((r) => r.input)};
  &:focus { outline: none; border-color: ${C((c) => c.primary)}; } &:disabled { opacity: 0.6; }
`;
var Helper = styled.p`margin: 0; font: 400 12px/15px ${F}; color: ${C((c) => c.text.muted)};`;
var EmptyCard = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 24px 20px; text-align: center; border-radius: 12px;
  background: ${C((c) => c.background.light)};
  & > .MuiSvgIcon-root { font-size: 28px; color: ${C((c) => c.text.muted)}; }
  & > strong { font: 600 14px/17px ${F}; color: ${C((c) => c.text.body)}; }
`;
var FileSummary = styled.div`
  margin-top: auto; display: flex; flex-direction: column; gap: 4px; padding: 14px 16px; border-radius: 12px; font: 400 12px/18px ${F}; color: ${C((c) => c.text.muted)};
  background: ${C((c) => c.background.light)};
  & > strong { font: 600 13px/17px ${F}; color: ${C((c) => c.text.body)}; }
`;
var RailSectionTitle = styled.h3`margin: 0; font: 600 14px/18px ${F}; color: ${C((c) => c.text.body)};`;
var RailDivider = styled.div`border-top: 1px solid ${C((c) => c.sectionDivider)};`;
var BoxList = styled.ul`list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px;`;
var BoxListItem = styled.li`list-style: none;`;
var BoxListButton = styled(BoxRow)`font-size: 13px;`;
var BoxReason = styled.span`overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
var PageTileList = styled.div`display: flex; flex-direction: column; gap: 4px;`;
var PageTile = styled.button`
  display: flex; align-items: center; gap: 14px; width: 100%; padding: 8px 10px; border-radius: 10px; cursor: pointer; text-align: left; font: ${(p) => p.$active ? 600 : 400} 13px/16px ${F};
  border: 1.5px solid ${(p) => p.$active ? T(p).colors.primary : "transparent"}; background: ${(p) => p.$active ? T(p).colors.primaryExtraLight : "transparent"}; color: ${C((c) => c.text.body)};
  &:hover { background: ${(p) => p.$active ? T(p).colors.primaryExtraLight : T(p).colors.background.light}; }
  &:focus:not(:focus-visible) { outline: none; }
`;
var Thumb = styled.div`
  width: 46px; height: 60px; flex: 0 0 auto; overflow: hidden; display: flex; align-items: center; justify-content: center;
  border: 1px solid ${C((c) => c.sectionDivider)}; background: ${C((c) => c.background.white)};
  & canvas { max-width: 100%; max-height: 100%; }
`;
var Dialog = styled(MuiDialog)`
  & .MuiBackdrop-root { background: ${C((c) => c.background.pageOverlay)}; }
  & .MuiDialog-container:focus, & .MuiPaper-root:focus, & .MuiPaper-root:focus-visible { outline: none; }
  & .MuiPaper-root { border-radius: 20px; padding: 30px; width: 560px; max-width: calc(100vw - 40px); box-shadow: none; font-family: ${F}; }
`;
var DialogTitle = styled.h2`margin: 0 0 16px; font: 600 18px/22px ${F}; color: ${C((c) => c.text.body)};`;
var DialogBody = styled.p`margin: 0; font: 400 14px/20px ${F}; color: ${C((c) => c.text.body)};`;
var DialogActions = styled.div`display: flex; justify-content: flex-end; align-items: center; gap: 12px; margin-top: 16px;`;
var TextInput = styled.input`
  display: block; width: 100%; box-sizing: border-box; height: 34px; padding: 0 12px; margin: 6px 0 12px; font: 400 14px ${F}; color: ${C((c) => c.text.body)};
  background: ${C((c) => c.background.medium)}; border: 1px solid ${C((c) => c.sectionDivider)}; border-radius: ${R((r) => r.input)};
  &:focus { outline: none; border-color: ${C((c) => c.primary)}; } &[aria-invalid="true"] { border-color: ${C((c) => c.error)}; }
  &[aria-invalid="true"]:focus { box-shadow: 0 0 0 3px ${C((c) => `${c.error}33`)}; }
  &:disabled { color: ${C((c) => c.text.body)}; opacity: 0.75; }
`;
var ChoiceRow = styled.label`display: flex; align-items: center; gap: 2px; margin-left: -9px; cursor: pointer; & .MuiButtonBase-root { padding: 6px; }`;
var ChoiceText = styled.span`font: 400 14px/18px ${F}; color: ${C((c) => c.text.body)};`;
var OptionRow = styled.div`display: flex; flex-wrap: wrap; gap: 0 18px; margin: -6px 0 8px;`;
var ErrorNote = styled.p`margin: -6px 0 12px; font: 400 12px/15px ${F}; color: ${C((c) => c.error)};`;
var Required = styled.span`color: ${C((c) => c.error)}; margin-left: 3px;`;

// src/usePdfDocument.js
import { useCallback, useEffect, useRef, useState } from "react";
function usePdfDocument({ pdfjs, loadDocument, lazyPages = false }) {
  const [state, setState] = useState({ doc: null, pages: [], pageCount: 0, loading: true, error: null, version: 0 });
  const docRef = useRef(null);
  const seqRef = useRef(0);
  const deps = useRef({ pdfjs, loadDocument });
  deps.current = { pdfjs, loadDocument };
  const lazyRef = useRef(lazyPages);
  lazyRef.current = lazyPages;
  const load = useCallback(async () => {
    const { pdfjs: pdfjs2, loadDocument: loadDocument2 } = deps.current;
    const seq = ++seqRef.current;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const source = await loadDocument2();
      if (seq !== seqRef.current) return;
      if (pdfjs2.workerSrc && pdfjs2.GlobalWorkerOptions && pdfjs2.GlobalWorkerOptions.workerSrc !== pdfjs2.workerSrc) {
        pdfjs2.GlobalWorkerOptions.workerSrc = pdfjs2.workerSrc;
      }
      const params = source.url ? { url: source.url } : { data: new Uint8Array(source.data.slice(0)) };
      const doc = await pdfjs2.getDocument({ ...params, isEvalSupported: false }).promise;
      if (seq !== seqRef.current) {
        doc.destroy?.();
        return;
      }
      const pages = [];
      const upfront = lazyRef.current ? Math.min(1, doc.numPages) : doc.numPages;
      for (let n = 1; n <= upfront; n += 1) pages.push(await doc.getPage(n));
      if (seq !== seqRef.current) {
        doc.destroy?.();
        return;
      }
      docRef.current?.destroy?.();
      docRef.current = doc;
      setState((prev) => ({ doc, pages, pageCount: doc.numPages, loading: false, error: null, version: prev.version + 1 }));
    } catch (e) {
      if (seq !== seqRef.current) return;
      setState((prev) => ({ ...prev, loading: false, error: e }));
    }
  }, []);
  const openPage = useCallback(async (index) => {
    const doc = docRef.current;
    if (!doc || !Number.isInteger(index) || index < 0 || index >= doc.numPages) return;
    const seq = seqRef.current;
    const page = await doc.getPage(index + 1);
    if (seq !== seqRef.current) return;
    setState((prev) => {
      if (prev.doc !== doc || prev.pages[index]) return prev;
      const pages = prev.pages.slice();
      pages[index] = page;
      return { ...prev, pages };
    });
  }, []);
  useEffect(() => {
    load();
    return () => {
      seqRef.current += 1;
      docRef.current?.destroy?.();
      docRef.current = null;
    };
  }, [load]);
  return { ...state, openPage, reload: load };
}

// src/usePageZoom.js
import { useEffect as useEffect2, useRef as useRef2, useState as useState2 } from "react";
var ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
var FIT_PADDING_PX = 60;
function nextZoom(current, direction) {
  const idx = ZOOM_STEPS.findIndex((z) => Math.abs(z - current) < 0.01);
  if (idx === -1) {
    return direction > 0 ? ZOOM_STEPS.find((z) => z > current) ?? current : [...ZOOM_STEPS].reverse().find((z) => z < current) ?? current;
  }
  return ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, idx + direction))];
}
function usePageZoom({ pageWidthPoints = 612, ready = false } = {}) {
  const [zoom, setZoom] = useState2("fit");
  const [fitWidth, setFitWidth] = useState2(900);
  const scrollRef = useRef2(null);
  useEffect2(() => {
    const el = scrollRef.current;
    if (!el) return void 0;
    const measure = () => setFitWidth(Math.max(320, el.clientWidth - FIT_PADDING_PX));
    measure();
    if (typeof ResizeObserver === "undefined") return void 0;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ready]);
  const scale = zoom === "fit" ? Math.min(1, fitWidth / pageWidthPoints) : zoom;
  const zoomPercent = Math.round(scale * 100);
  return {
    zoom,
    scale,
    zoomPercent,
    scrollRef,
    isFit: zoom === "fit",
    canZoomOut: zoomPercent > ZOOM_STEPS[0] * 100,
    canZoomIn: zoomPercent < ZOOM_STEPS[ZOOM_STEPS.length - 1] * 100,
    zoomIn: () => setZoom(nextZoom(scale, 1)),
    zoomOut: () => setZoom(nextZoom(scale, -1)),
    fitToWidth: () => setZoom("fit")
  };
}

// src/usePageNavigation.js
import { useCallback as useCallback2, useState as useState3 } from "react";
function usePageNavigation(pageCount, { onChange } = {}) {
  const [pageIndex, setPageIndex] = useState3(0);
  const [pageDraft, setPageDraft] = useState3(null);
  const [navigationKey, setNavigationKey] = useState3(0);
  const current = Math.min(pageIndex, Math.max(pageCount - 1, 0));
  const goToPage = useCallback2((n) => {
    const target = Math.min(Math.max(n, 1), Math.max(pageCount, 1));
    setPageIndex(target - 1);
    onChange?.(target);
    setNavigationKey((k) => k + 1);
  }, [pageCount, onChange]);
  const commitPageDraft = useCallback2(() => {
    if (pageDraft !== null) goToPage(parseInt(pageDraft, 10) || current + 1);
    setPageDraft(null);
  }, [pageDraft, goToPage, current]);
  const setVisiblePage = useCallback2((index) => {
    const target = Math.min(Math.max(index, 0), Math.max(pageCount - 1, 0));
    setPageIndex((prev) => {
      if (prev !== target) onChange?.(target + 1);
      return target;
    });
  }, [pageCount, onChange]);
  return { pageIndex: current, pageDraft, navigationKey, setPageDraft, commitPageDraft, goToPage, setVisiblePage };
}

// src/useDocumentSearch.js
import { useState as useState4 } from "react";

// src/annotationMath.js
var MIN_BOX_POINTS = 2;
function round2(value) {
  return Math.round(value * 100) / 100;
}
function pointsToPx(box, scale) {
  return { left: box.x * scale, top: box.y * scale, width: box.w * scale, height: box.h * scale };
}
function pxToPoints(px, scale) {
  return { x: round2(px.left / scale), y: round2(px.top / scale), w: round2(px.width / scale), h: round2(px.height / scale) };
}
function normalizeRect([x1, y1, x2, y2]) {
  const x = Math.min(x1, x2);
  const y = Math.min(y1, y2);
  return { x: round2(x), y: round2(y), w: round2(Math.abs(x2 - x1)), h: round2(Math.abs(y2 - y1)) };
}
function clampBox(box, pageWidth, pageHeight) {
  const w = Math.min(Math.max(box.w, MIN_BOX_POINTS), pageWidth);
  const h = Math.min(Math.max(box.h, MIN_BOX_POINTS), pageHeight);
  const x = Math.min(Math.max(box.x, 0), pageWidth - w);
  const y = Math.min(Math.max(box.y, 0), pageHeight - h);
  return { ...box, x: round2(x), y: round2(y), w: round2(w), h: round2(h) };
}
function createIdFactory(prefix = "box") {
  const salt = Math.random().toString(36).slice(2, 8);
  let n = 0;
  return () => {
    n += 1;
    return `${prefix}-${salt}-${n}`;
  };
}
var passThrough = (r) => ({ x: round2(r.x), y: round2(r.y), w: round2(r.w), h: round2(r.h) });
var hasViewport = (viewport) => typeof viewport?.convertToPdfPoint === "function" && Array.isArray(viewport.viewBox);
function boxToUnrotatedRect(box, viewport) {
  if (!hasViewport(viewport)) return passThrough(box);
  const [ax, ay] = viewport.convertToPdfPoint(box.x, box.y);
  const [bx, by] = viewport.convertToPdfPoint(box.x + box.w, box.y + box.h);
  const [viewLeft, , , viewTop] = viewport.viewBox;
  return {
    x: round2(Math.min(ax, bx) - viewLeft),
    y: round2(viewTop - Math.max(ay, by)),
    w: round2(Math.abs(bx - ax)),
    h: round2(Math.abs(by - ay))
  };
}
function unrotatedRectToBox(rect, viewport) {
  if (!hasViewport(viewport)) return passThrough(rect);
  const [viewLeft, , , viewTop] = viewport.viewBox;
  const pdfRect = [viewLeft + rect.x, viewTop - (rect.y + rect.h), viewLeft + rect.x + rect.w, viewTop - rect.y];
  return normalizeRect(viewport.convertToViewportRectangle(pdfRect));
}
var viewportOf = (pages, page) => pages[page]?.getViewport?.({ scale: 1 });
var originOf = (origin) => origin === "manual" ? "manual" : "ai";
function boxesToPayload(boxes, pages = []) {
  return boxes.map(({ id, page, x, y, w, h, reason, origin }) => ({
    id,
    page,
    ...boxToUnrotatedRect({ x, y, w, h }, viewportOf(pages, page)),
    reason: reason ?? "",
    origin: originOf(origin)
  }));
}
function payloadToBoxes(rows, pages = [], newId = createIdFactory()) {
  return rows.map(({ id, page, x, y, w, h, reason, origin, text }) => ({
    id: id ?? newId(),
    page,
    ...unrotatedRectToBox({ x, y, w, h }, viewportOf(pages, page)),
    reason: reason ?? "",
    origin: originOf(origin),
    ...text ? { text } : {}
  }));
}
function serializeBoxes(boxes) {
  return JSON.stringify(
    boxes.map((b) => [b.page, round2(b.x), round2(b.y), round2(b.w), round2(b.h), b.reason ?? "", b.origin ?? "ai"]).sort((a, b) => a < b ? -1 : a > b ? 1 : 0)
  );
}
var sameGeometry = (a, b) => a.page === b.page && ["x", "y", "w", "h"].every((k) => round2(a[k]) === round2(b[k]));
function changeSummary(saved, current) {
  const savedById = new Map(saved.map((b) => [b.id, b]));
  let added = 0;
  let moved = 0;
  let reworded = 0;
  for (const box of current) {
    const was = savedById.get(box.id);
    if (!was) added += 1;
    else if (!sameGeometry(was, box)) moved += 1;
    else if ((was.reason ?? "") !== (box.reason ?? "")) reworded += 1;
  }
  const ids = new Set(current.map((b) => b.id));
  const deleted = saved.filter((b) => !ids.has(b.id)).length;
  return { added, moved, reworded, deleted, total: added + moved + reworded + deleted };
}
function textUnderBox(textContent, viewport, box, { maxLength = 200 } = {}) {
  const parts = [];
  for (const item of textContent?.items ?? []) {
    const str = item.str ?? "";
    if (!str.trim() || !Array.isArray(item.transform) || !(item.width > 0)) continue;
    const [, , , , originX, originY] = item.transform;
    const rect = normalizeRect(viewport.convertToViewportRectangle([originX, originY, originX + item.width, originY + (item.height || 0)]));
    const centreY = rect.y + rect.h / 2;
    if (centreY < box.y || centreY > box.y + box.h) continue;
    const overlapLeft = Math.max(rect.x, box.x);
    const overlapRight = Math.min(rect.x + rect.w, box.x + box.w);
    if (overlapRight - overlapLeft <= 0) continue;
    const start = Math.max(0, Math.floor((overlapLeft - rect.x) / rect.w * str.length));
    const end = Math.min(str.length, Math.ceil((overlapRight - rect.x) / rect.w * str.length));
    const slice = str.slice(start, end).trim();
    if (slice) parts.push(slice);
  }
  return parts.join(" ").replace(/\s+/g, " ").replace(/^[\s\p{P}]+|[\s\p{P}]+$/gu, "").slice(0, maxLength);
}

// src/documentSearch.js
var DEFAULT_FONT_ASCENT = 0.8;
var DEFAULT_FONT_DESCENT = -0.2;
var HIT_PADDING_POINTS = 1;
function offsets(text, startOffset, endOffset, fontStyle, measure) {
  if (measure) {
    const font = `100px ${fontStyle?.fontFamily || "sans-serif"}`;
    const total = measure(text, font);
    if (total > 0) return { startRatio: measure(text.slice(0, startOffset), font) / total, endRatio: measure(text.slice(0, endOffset), font) / total };
  }
  return { startRatio: startOffset / text.length, endRatio: endOffset / text.length };
}
function itemRect(item, fontStyle, viewport, startOffset, endOffset, measure) {
  if (!Array.isArray(item.transform) || !(item.width > 0) || !item.str?.length) return null;
  const [, , , , originX, originY] = item.transform;
  const textHeight = item.height || Math.hypot(item.transform[2], item.transform[3]) || Math.hypot(item.transform[0], item.transform[1]);
  const ascent = Number.isFinite(fontStyle?.ascent) ? fontStyle.ascent : DEFAULT_FONT_ASCENT;
  const descent = Number.isFinite(fontStyle?.descent) ? fontStyle.descent : DEFAULT_FONT_DESCENT;
  const full = normalizeRect(viewport.convertToViewportRectangle([originX, originY + descent * textHeight, originX + item.width, originY + ascent * textHeight]));
  const { startRatio, endRatio } = offsets(item.str, startOffset, endOffset, fontStyle, measure);
  const portion = endRatio - startRatio;
  if (viewport.rotation === 90) return { x: full.x, y: full.y + full.h * startRatio - HIT_PADDING_POINTS, w: full.w, h: full.h * portion + HIT_PADDING_POINTS * 2 };
  if (viewport.rotation === 180) return { x: full.x + full.w * (1 - endRatio) - HIT_PADDING_POINTS, y: full.y, w: full.w * portion + HIT_PADDING_POINTS * 2, h: full.h };
  if (viewport.rotation === 270) return { x: full.x, y: full.y + full.h * (1 - endRatio) - HIT_PADDING_POINTS, w: full.w, h: full.h * portion + HIT_PADDING_POINTS * 2 };
  return { x: full.x + full.w * startRatio - HIT_PADDING_POINTS, y: full.y, w: full.w * portion + HIT_PADDING_POINTS * 2, h: full.h };
}
async function findDocumentMatches(pages, query, { measure } = {}) {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return [];
  const matches = [];
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const page = pages[pageIndex];
    const content = await page.getTextContent();
    const items = (content?.items ?? []).filter((item) => item.str);
    const ranges = [];
    let text = "";
    items.forEach((item, index) => {
      if (index > 0) text += " ";
      const start = text.length;
      text += item.str;
      ranges.push({ item, start, end: text.length });
    });
    const haystack = text.toLocaleLowerCase();
    const viewport = page.getViewport({ scale: 1 });
    let from = 0;
    while (from <= haystack.length - needle.length) {
      const start = haystack.indexOf(needle, from);
      if (start < 0) break;
      const end = start + needle.length;
      const rects = ranges.filter((range) => range.end > start && range.start < end).map((range) => itemRect(range.item, content.styles?.[range.item.fontName], viewport, Math.max(start, range.start) - range.start, Math.min(end, range.end) - range.start, measure)).filter(Boolean);
      if (rects.length) matches.push({ page: pageIndex, rects });
      from = start + Math.max(needle.length, 1);
    }
  }
  return matches;
}

// src/useDocumentSearch.js
function useDocumentSearch({ pages, pageNavigation, rootRef }) {
  const [isOpen, setIsOpen] = useState4(false);
  const [query, setQuery] = useState4("");
  const [isSearching, setIsSearching] = useState4(false);
  const [hasSearched, setHasSearched] = useState4(false);
  const [results, setResults] = useState4([]);
  const [activeIndex, setActiveIndex] = useState4(0);
  function clearResults() {
    setHasSearched(false);
    setResults([]);
    setActiveIndex(0);
  }
  function open() {
    setQuery("");
    clearResults();
    setIsOpen(true);
  }
  function close() {
    setIsOpen(false);
    setQuery("");
    clearResults();
  }
  function updateQuery(value) {
    setQuery(value);
    clearResults();
  }
  function showResult(index, list = results) {
    if (!list.length) return;
    const next = (index + list.length) % list.length;
    setActiveIndex(next);
    pageNavigation.goToPage(list[next].page + 1);
  }
  function measurer() {
    const ctx = rootRef?.current?.ownerDocument?.createElement("canvas")?.getContext?.("2d");
    if (!ctx) return void 0;
    return (text, font) => {
      ctx.font = font;
      return ctx.measureText(text).width;
    };
  }
  async function search(event) {
    event?.preventDefault?.();
    if (!query.trim() || isSearching) return;
    setIsSearching(true);
    try {
      const next = await findDocumentMatches(pages, query, { measure: measurer() });
      setResults(next);
      setHasSearched(true);
      setActiveIndex(0);
      if (next.length) pageNavigation.goToPage(next[0].page + 1);
    } finally {
      setIsSearching(false);
    }
  }
  return {
    isOpen,
    query,
    isSearching,
    hasSearched,
    results,
    activeIndex,
    open,
    close,
    updateQuery,
    search,
    showPrevious: () => showResult(activeIndex - 1),
    showNext: () => showResult(activeIndex + 1)
  };
}

// src/ContinuousPdfPages.js
import { useCallback as useCallback3, useEffect as useEffect4, useRef as useRef4, useState as useState6 } from "react";
import styled3 from "@emotion/styled";

// src/PdfPageCanvas.js
import { useEffect as useEffect3, useRef as useRef3, useState as useState5 } from "react";
import styled2 from "@emotion/styled";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var Frame = styled2.div`position: relative; flex: 0 0 auto; background: ${(p) => p.theme.tokens.colors.background.white};`;
var Canvas = styled2.canvas`display: block;`;
var Overlay = styled2.div`position: absolute; inset: 0;`;
function PdfPageCanvas({ page, scale, renderOverlay, paintAnnotations = false, testId = "pdf-page" }) {
  const canvasRef = useRef3(null);
  const [dims, setDims] = useState5(null);
  useEffect3(() => {
    if (!page) return void 0;
    let cancelled = false;
    const unscaled = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale });
    const canvas = canvasRef.current;
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const task = page.render({ canvasContext: canvas.getContext("2d"), viewport, annotationMode: paintAnnotations ? 1 : 0 });
    const next = { width: viewport.width, height: viewport.height, scale, pageWidth: unscaled.width, pageHeight: unscaled.height };
    task.promise.then(() => {
      if (!cancelled) setDims(next);
    }).catch(() => {
    });
    return () => {
      cancelled = true;
      task.cancel?.();
    };
  }, [page, scale, paintAnnotations]);
  return /* @__PURE__ */ jsxs(Frame, { "data-testid": testId, "data-page-number": page?.pageNumber, style: dims ? { width: dims.width, height: dims.height } : void 0, children: [
    /* @__PURE__ */ jsx2(Canvas, { ref: canvasRef }),
    dims && renderOverlay && /* @__PURE__ */ jsx2(Overlay, { "data-testid": "pdf-overlay", style: { width: dims.width, height: dims.height }, children: renderOverlay(dims) })
  ] });
}

// src/ContinuousPdfPages.js
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var Placeholder = styled3.div`position: relative; flex: 0 0 auto; background: ${(p) => p.theme.tokens.colors.background.white};`;
var Slot = styled3.div`display: flex; flex-direction: column; align-items: center; gap: 8px; scroll-margin-top: 24px;`;
function LazyPdfPage({ page, pageIndex, scale, active, scrollRoot, setPageRef, renderOverlay, paintAnnotations = false, testId = "pdf-page", placeholderTestId = "pdf-page-placeholder" }) {
  const [shouldRender, setShouldRender] = useState6(active);
  const wrapperRef = useRef4(null);
  const viewport = page.getViewport({ scale });
  useEffect4(() => {
    if (!setPageRef) return void 0;
    setPageRef(pageIndex, wrapperRef.current);
    return () => setPageRef(pageIndex, null);
  }, [pageIndex, setPageRef]);
  useEffect4(() => {
    if (active) setShouldRender(true);
  }, [active]);
  useEffect4(() => {
    const element = wrapperRef.current;
    if (shouldRender || !element || !scrollRoot || typeof IntersectionObserver === "undefined") return void 0;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setShouldRender(true);
        observer.disconnect();
      }
    }, { root: scrollRoot, rootMargin: "100% 0px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [scrollRoot, shouldRender]);
  return /* @__PURE__ */ jsx3(Placeholder, { ref: wrapperRef, "data-testid": placeholderTestId, "data-page-number": pageIndex + 1, style: { width: viewport.width, height: viewport.height }, children: shouldRender && /* @__PURE__ */ jsx3(PdfPageCanvas, { page, scale, renderOverlay: renderOverlay && ((dims) => renderOverlay(pageIndex, dims)), paintAnnotations, testId }) });
}
function ContinuousPdfPages({ pages, scale, pageIndex, navigationKey = 0, scrollRef, onPageChange = () => {
}, renderOverlay, paintAnnotations = false, numbered = false }) {
  const pageRefs = useRef4(/* @__PURE__ */ new Map());
  const [scrollRoot, setScrollRoot] = useState6(null);
  const onPageChangeRef = useRef4(onPageChange);
  onPageChangeRef.current = onPageChange;
  useEffect4(() => {
    setScrollRoot(scrollRef.current);
  }, [scrollRef, pages.length]);
  const lastJump = useRef4({ navigationKey, scale });
  useEffect4(() => {
    const changed = lastJump.current.navigationKey !== navigationKey || lastJump.current.scale !== scale;
    lastJump.current = { navigationKey, scale };
    const target = pageRefs.current.get(pageIndex);
    if (!changed || !scrollRef.current || !target) return;
    target.parentElement?.scrollIntoView?.({ block: "start" });
  }, [navigationKey, pages.length, scale]);
  useEffect4(() => {
    const root = scrollRef.current;
    if (!root) return void 0;
    let frame = null;
    const updatePage = () => {
      frame = null;
      const focusY = root.getBoundingClientRect().top + Math.min(root.clientHeight * 0.35, 240);
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      pageRefs.current.forEach((element, index) => {
        if (!element) return;
        const rect = element.getBoundingClientRect();
        const distance = focusY < rect.top ? rect.top - focusY : focusY > rect.bottom ? focusY - rect.bottom : 0;
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      onPageChangeRef.current(closestIndex);
    };
    const handleScroll = () => {
      if (frame === null) frame = requestAnimationFrame(updatePage);
    };
    root.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", handleScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [scrollRef, pages.length]);
  const setPageRef = useCallback3((index, element) => {
    if (element) pageRefs.current.set(index, element);
    else pageRefs.current.delete(index);
  }, []);
  return pages.map((pdfPage, index) => /* @__PURE__ */ jsxs2(Slot, { children: [
    /* @__PURE__ */ jsx3(LazyPdfPage, { page: pdfPage, pageIndex: index, scale, active: index === pageIndex, scrollRoot, setPageRef, renderOverlay, paintAnnotations }),
    numbered && /* @__PURE__ */ jsx3(PageNumber, { children: index + 1 })
  ] }, pdfPage.pageNumber ?? index));
}

// src/RedactionLayer.js
import { useEffect as useEffect5, useRef as useRef5, useState as useState7 } from "react";
import styled4 from "@emotion/styled";
import { useTheme } from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Fragment, jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var HANDLE_PX = 12;
var MIN_HANDLE_PX = 6;
var MIN_DRAW_PX = 6;
var HANDLES = [
  { key: "nw", x: 0, y: 0, cursor: "nwse-resize" },
  { key: "n", x: 0.5, y: 0, cursor: "ns-resize" },
  { key: "ne", x: 1, y: 0, cursor: "nesw-resize" },
  { key: "e", x: 1, y: 0.5, cursor: "ew-resize" },
  { key: "se", x: 1, y: 1, cursor: "nwse-resize" },
  { key: "s", x: 0.5, y: 1, cursor: "ns-resize" },
  { key: "sw", x: 0, y: 1, cursor: "nesw-resize" },
  { key: "w", x: 0, y: 0.5, cursor: "ew-resize" }
];
function handleLayout(widthPx, heightPx) {
  const size = Math.max(MIN_HANDLE_PX, Math.min(HANDLE_PX, Math.floor(Math.min(widthPx, heightPx) / 3)));
  const showNS = heightPx >= 3 * HANDLE_PX;
  const showEW = widthPx >= 3 * HANDLE_PX;
  return { size, handles: HANDLES.filter((h) => h.key.length === 2 ? true : h.key === "n" || h.key === "s" ? showNS : showEW) };
}
var Layer = styled4.div`position: absolute; inset: 0; user-select: none; cursor: ${(p) => p.$tool === "add" ? "crosshair" : "default"};`;
var Box = styled4.div`position: absolute; box-sizing: border-box;`;
var Handle = styled4.div`position: absolute; box-sizing: border-box; border-radius: 2px; background: ${(p) => p.theme.tokens.colors.white}; border: 2px solid ${(p) => p.theme.tokens.colors.primary};`;
var Label = styled4.div`
  position: absolute; top: 100%; margin-top: 4px; padding: 3px 8px; border-radius: 3px; pointer-events: none; white-space: nowrap; display: inline-flex; align-items: center; gap: 4px;
  & .MuiSvgIcon-root { font-size: 12px; }
  background: ${(p) => p.theme.tokens.colors.primary}; color: ${(p) => p.theme.tokens.colors.white}; font-size: 11px; line-height: 14px;
`;
function RedactionLayer({ pageIndex, dims, boxes, tool = "add", editable = true, selectedId = null, onSelect = () => {
}, onCreate = () => {
}, onUpdate = () => {
} }) {
  const { tokens } = useTheme();
  const ref = useRef5(null);
  const drag = useRef5(null);
  const [draft, setDraftState] = useState7(null);
  const draftRef = useRef5(null);
  const colors = {
    ai: { fill: aiFill(tokens.colors), stroke: aiStroke(tokens.colors) },
    manual: { fill: manualFill(tokens.colors), stroke: tokens.colors.primary },
    selected: tokens.colors.primary
  };
  function setDraft(next) {
    draftRef.current = typeof next === "function" ? next(draftRef.current) : next;
    setDraftState(draftRef.current);
  }
  const onPage = boxes.filter((b) => b.page === pageIndex);
  function localPoint(e) {
    const rect = ref.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }
  function onBackgroundMouseDown(e) {
    if (e.button !== 0 || e.target !== ref.current) return;
    if (!editable || tool !== "add") {
      onSelect(null);
      return;
    }
    const p = localPoint(e);
    drag.current = { mode: "draw", startX: p.x, startY: p.y };
    setDraft({ left: p.x, top: p.y, width: 0, height: 0 });
    e.preventDefault();
  }
  function beginMove(e, box) {
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelect(box.id);
    if (!editable) return;
    const p = localPoint(e);
    const px = pointsToPx(box, dims.scale);
    drag.current = { mode: "move", id: box.id, dx: p.x - px.left, dy: p.y - px.top, start: px, moved: false };
    setDraft(px);
    e.preventDefault();
  }
  function beginResize(e, box, handle) {
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelect(box.id);
    if (!editable) return;
    const px = pointsToPx(box, dims.scale);
    drag.current = { mode: "resize", id: box.id, handle, start: px, moved: false };
    setDraft(px);
    e.preventDefault();
  }
  useEffect5(() => {
    const doc = ref.current?.ownerDocument;
    if (!doc) return void 0;
    function onMove(e) {
      const d = drag.current;
      if (!d) return;
      const p = localPoint(e);
      if (d.mode === "draw") {
        setDraft({ left: Math.min(p.x, d.startX), top: Math.min(p.y, d.startY), width: Math.abs(p.x - d.startX), height: Math.abs(p.y - d.startY) });
      } else if (d.mode === "move") {
        d.moved = true;
        setDraft((cur) => ({ ...cur, left: p.x - d.dx, top: p.y - d.dy }));
      } else if (d.mode === "resize") {
        d.moved = true;
        const s = d.start;
        const minPx = MIN_BOX_POINTS * dims.scale;
        let { left, top } = s;
        let right = s.left + s.width;
        let bottom = s.top + s.height;
        if (d.handle.x === 0) left = Math.min(p.x, right - minPx);
        if (d.handle.x === 1) right = Math.max(p.x, left + minPx);
        if (d.handle.y === 0) top = Math.min(p.y, bottom - minPx);
        if (d.handle.y === 1) bottom = Math.max(p.y, top + minPx);
        setDraft({ left, top, width: right - left, height: bottom - top });
      }
    }
    function onUp() {
      const d = drag.current;
      if (!d) return;
      drag.current = null;
      const cur = draftRef.current;
      setDraft(null);
      if (!cur) return;
      if (d.mode === "draw") {
        if (cur.width > MIN_DRAW_PX && cur.height > MIN_DRAW_PX) onCreate(clampBox({ page: pageIndex, ...pxToPoints(cur, dims.scale) }, dims.pageWidth, dims.pageHeight));
      } else if (d.moved) {
        const { x, y, w, h } = clampBox(pxToPoints(cur, dims.scale), dims.pageWidth, dims.pageHeight);
        onUpdate(d.id, { x, y, w, h });
      }
    }
    doc.addEventListener("mousemove", onMove);
    doc.addEventListener("mouseup", onUp);
    return () => {
      doc.removeEventListener("mousemove", onMove);
      doc.removeEventListener("mouseup", onUp);
    };
  }, [dims, pageIndex, onCreate, onUpdate]);
  return /* @__PURE__ */ jsxs3(Layer, { ref, "data-testid": "redaction-layer", $tool: editable ? tool : "select", onMouseDown: onBackgroundMouseDown, children: [
    onPage.map((box) => {
      const px = draft && drag.current?.id === box.id ? draft : pointsToPx(box, dims.scale);
      const selected = selectedId === box.id;
      const palette = colors[box.origin === "manual" ? "manual" : "ai"];
      return /* @__PURE__ */ jsx4(
        Box,
        {
          "data-testid": "redaction-box",
          "data-box-id": box.id,
          "data-origin": box.origin,
          "data-text": box.text,
          "data-selected": selected ? "true" : "false",
          role: "button",
          "aria-label": `Redaction box: ${box.reason || "no reason"}`,
          "aria-pressed": selected,
          tabIndex: 0,
          title: box.reason,
          onMouseDown: (e) => beginMove(e, box),
          onClick: (e) => e.stopPropagation(),
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect(box.id);
            }
          },
          style: {
            left: px.left,
            top: px.top,
            width: px.width,
            height: px.height,
            background: palette.fill,
            border: `${selected ? 2 : 1.5}px solid ${selected ? colors.selected : palette.stroke}`,
            cursor: editable ? "move" : "pointer",
            zIndex: selected ? 2 : 1
          },
          children: selected && editable && (() => {
            const { size, handles } = handleLayout(px.width, px.height);
            return /* @__PURE__ */ jsxs3(Fragment, { children: [
              handles.map((handle) => /* @__PURE__ */ jsx4(
                Handle,
                {
                  "data-testid": `resize-handle-${handle.key}`,
                  onMouseDown: (e) => beginResize(e, box, handle),
                  style: { width: size, height: size, left: `calc(${handle.x * 100}% - ${size / 2}px)`, top: `calc(${handle.y * 100}% - ${size / 2}px)`, cursor: handle.cursor }
                },
                handle.key
              )),
              /* @__PURE__ */ jsxs3(Label, { "data-testid": "redaction-box-label", style: px.left + px.width / 2 > dims.width * 0.6 ? { right: 0 } : { left: 0 }, children: [
                box.origin === "manual" ? /* @__PURE__ */ jsx4(EditOutlinedIcon, {}) : /* @__PURE__ */ jsx4(AutoAwesomeOutlinedIcon, {}),
                /* @__PURE__ */ jsxs3("span", { children: [
                  box.origin === "manual" ? "Manual" : "AI suggestion",
                  box.reason ? ` \xB7 ${box.reason}` : ""
                ] })
              ] })
            ] });
          })()
        },
        box.id
      );
    }),
    draft && drag.current?.mode === "draw" && /* @__PURE__ */ jsx4(Box, { "data-testid": "redaction-draft", style: { left: draft.left, top: draft.top, width: draft.width, height: draft.height, background: `${tokens.colors.primary}33`, border: `2px dashed ${tokens.colors.primary}` } })
  ] });
}

// src/DocumentSearchHighlights.js
import styled5 from "@emotion/styled";
import { jsx as jsx5 } from "react/jsx-runtime";
var Highlight = styled5.span`
  position: absolute; z-index: 3; pointer-events: none; box-sizing: border-box; border-radius: 2px; mix-blend-mode: multiply;
  background: ${(p) => `${p.theme.tokens.colors.status.warning}66`};
  &[data-active='true'] { background: ${(p) => `${p.theme.tokens.colors.status.warning}9E`}; box-shadow: 0 0 0 2px ${(p) => p.theme.tokens.colors.status.warning}; }
`;
function DocumentSearchHighlights({ matches, activeIndex, scale }) {
  return matches.flatMap((match) => match.rects.map((rect, i) => /* @__PURE__ */ jsx5(Highlight, { "data-testid": "document-search-highlight", "data-active": match.index === activeIndex ? "true" : "false", style: pointsToPx(rect, scale) }, `${match.index}-${i}`)));
}

// src/DocumentSearchToolbar.js
import { useEffect as useEffect6, useRef as useRef6 } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { Fragment as Fragment2, jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function DocumentSearchToolbar({ search }) {
  const formRef = useRef6(null);
  const { isOpen, close } = search;
  useEffect6(() => {
    if (!isOpen) return void 0;
    const doc = formRef.current?.ownerDocument;
    if (!doc) return void 0;
    const onOutside = (event) => {
      if (!formRef.current?.contains(event.target)) close();
    };
    doc.addEventListener("mousedown", onOutside);
    return () => doc.removeEventListener("mousedown", onOutside);
  }, [isOpen, close]);
  if (!isOpen) {
    return /* @__PURE__ */ jsxs4(ToolButton, { onClick: search.open, "aria-label": "Search document", "data-testid": "search-open", children: [
      /* @__PURE__ */ jsx6(SearchIcon, {}),
      " Search"
    ] });
  }
  const { query, isSearching, hasSearched, results, activeIndex } = search;
  return /* @__PURE__ */ jsxs4(ToolGroup, { as: "form", ref: formRef, role: "search", onSubmit: (e) => {
    e.preventDefault();
    search.search(e);
  }, children: [
    /* @__PURE__ */ jsx6(
      SearchInput,
      {
        autoFocus: true,
        "aria-label": "Search document text",
        placeholder: "Search this document",
        value: query,
        onChange: (e) => search.updateQuery(e.target.value),
        onKeyDown: (e) => {
          if (e.key === "Enter" && hasSearched && results.length && !isSearching) {
            e.preventDefault();
            search.showNext();
          }
        },
        readOnly: isSearching,
        "aria-busy": isSearching,
        "data-testid": "search-input"
      }
    ),
    /* @__PURE__ */ jsx6(SquareIconButton, { type: "submit", "aria-label": "Run document search", disabled: !query.trim() || isSearching, "data-testid": "search-run", children: /* @__PURE__ */ jsx6(SearchIcon, {}) }),
    hasSearched && /* @__PURE__ */ jsxs4(Fragment2, { children: [
      /* @__PURE__ */ jsx6(ToolText, { role: "status", "data-testid": "search-status", children: results.length ? `${activeIndex + 1} of ${results.length}` : "No results" }),
      /* @__PURE__ */ jsx6(SquareIconButton, { type: "button", "aria-label": "Previous search result", onClick: search.showPrevious, disabled: !results.length, "data-testid": "search-prev", children: /* @__PURE__ */ jsx6(ChevronLeftIcon, {}) }),
      /* @__PURE__ */ jsx6(SquareIconButton, { type: "button", "aria-label": "Next search result", onClick: search.showNext, disabled: !results.length, "data-testid": "search-next", children: /* @__PURE__ */ jsx6(ChevronRightIcon, {}) }),
      /* @__PURE__ */ jsx6(SquareIconButton, { type: "button", "aria-label": "Close document search", onClick: close, "data-testid": "search-close", children: /* @__PURE__ */ jsx6(CloseIcon, {}) })
    ] })
  ] });
}

// src/ZoomPageToolbar.js
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import FitScreenOutlinedIcon from "@mui/icons-material/FitScreenOutlined";
import ChevronLeftIcon2 from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon2 from "@mui/icons-material/ChevronRight";
import { Fragment as Fragment3, jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function ZoomPageToolbar({ zoom, page, pageCount }) {
  return /* @__PURE__ */ jsxs5(Fragment3, { children: [
    /* @__PURE__ */ jsxs5(ToolGroup, { role: "group", "aria-label": "Zoom", children: [
      /* @__PURE__ */ jsx7(SquareIconButton, { "aria-label": "Zoom out", onClick: zoom.zoomOut, disabled: !zoom.canZoomOut, "data-testid": "zoom-out", children: /* @__PURE__ */ jsx7(RemoveIcon, {}) }),
      /* @__PURE__ */ jsxs5(ZoomLabel, { "data-testid": "zoom-level", children: [
        zoom.zoomPercent,
        " %"
      ] }),
      /* @__PURE__ */ jsx7(SquareIconButton, { "aria-label": "Zoom in", onClick: zoom.zoomIn, disabled: !zoom.canZoomIn, "data-testid": "zoom-in", children: /* @__PURE__ */ jsx7(AddIcon, {}) }),
      /* @__PURE__ */ jsx7(SquareIconButton, { "aria-label": "Fit to width", onClick: zoom.fitToWidth, $active: zoom.isFit, "aria-pressed": zoom.isFit, "data-testid": "zoom-fit", children: /* @__PURE__ */ jsx7(FitScreenOutlinedIcon, {}) })
    ] }),
    /* @__PURE__ */ jsx7(Sep, {}),
    /* @__PURE__ */ jsxs5(ToolGroup, { role: "group", "aria-label": "Page navigation", children: [
      /* @__PURE__ */ jsx7(SquareIconButton, { "aria-label": "Previous page", onClick: () => page.goToPage(page.pageIndex), disabled: page.pageIndex === 0, "data-testid": "page-prev", children: /* @__PURE__ */ jsx7(ChevronLeftIcon2, {}) }),
      /* @__PURE__ */ jsx7(ToolText, { children: "Page" }),
      /* @__PURE__ */ jsx7(
        PageInput,
        {
          "aria-label": "Page number",
          value: page.pageDraft ?? String(page.pageIndex + 1),
          onChange: (e) => page.setPageDraft(e.target.value),
          onBlur: page.commitPageDraft,
          onKeyDown: (e) => {
            if (e.key === "Enter") page.commitPageDraft();
          },
          "data-testid": "page-input"
        }
      ),
      /* @__PURE__ */ jsxs5(ToolText, { "data-testid": "page-count", children: [
        "of ",
        pageCount
      ] }),
      /* @__PURE__ */ jsx7(SquareIconButton, { "aria-label": "Next page", onClick: () => page.goToPage(page.pageIndex + 2), disabled: page.pageIndex >= pageCount - 1, "data-testid": "page-next", children: /* @__PURE__ */ jsx7(ChevronRightIcon2, {}) })
    ] })
  ] });
}

// src/SelectedBoxPanel.js
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { useTheme as useTheme2 } from "@mui/material/styles";

// src/redactionLimits.js
var MAX_REDACTION_ANNOTATIONS = 5e3;
var MAX_REDACTION_REASON_LENGTH = 2e3;
var REDACTION_SEARCH_TEXT_LENGTH = Object.freeze({ min: 2, max: 200 });
var DEFAULT_LIMITS = Object.freeze({
  maxBoxes: MAX_REDACTION_ANNOTATIONS,
  maxReasonLength: MAX_REDACTION_REASON_LENGTH,
  searchText: REDACTION_SEARCH_TEXT_LENGTH
});
var withDefaultLimits = (limits) => ({
  ...DEFAULT_LIMITS,
  ...limits,
  searchText: { ...DEFAULT_LIMITS.searchText, ...limits?.searchText || {} }
});

// src/SelectedBoxPanel.js
import { Fragment as Fragment4, jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var timeOf = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).replace(" ", "\xA0") : null;
var plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
function BoxEntry({ box, selectedId, onSelect, colors }) {
  const manual = box.origin === "manual";
  return /* @__PURE__ */ jsx8(BoxListItem, { children: /* @__PURE__ */ jsxs6(
    BoxListButton,
    {
      type: "button",
      $selected: box.id === selectedId,
      "aria-pressed": box.id === selectedId,
      onClick: () => onSelect(box.id),
      "data-testid": "rail-box",
      "data-box-id": box.id,
      children: [
        /* @__PURE__ */ jsx8(Swatch, { $fill: manual ? manualFill(colors) : aiFill(colors), $stroke: manual ? colors.primary : aiStroke(colors) }),
        /* @__PURE__ */ jsx8(BoxReason, { children: box.reason || "No reason yet" })
      ]
    }
  ) });
}
function SelectedBoxPanel({
  selected,
  boxes = [],
  currentPage = 0,
  selectedId,
  pageCount,
  editable,
  maxReasonLength = MAX_REDACTION_REASON_LENGTH,
  onReason,
  onReasonBlur,
  onDelete,
  onSelect = () => {
  },
  counts,
  version,
  dirty,
  changes
}) {
  const { tokens } = useTheme2();
  const saved = timeOf(version);
  const rows = [...boxes].sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x);
  const onThisPage = rows.filter((box) => box.page === currentPage);
  const pageGroups = [...new Set(rows.map((box) => box.page))];
  const otherPages = pageGroups.filter((page) => page !== currentPage);
  const summary = [`${counts.ai} suggested`, `${counts.manual} manual`];
  if (dirty && changes?.added) summary.push(`${changes.added} added since last save`);
  if (dirty && changes?.deleted) summary.push(`${changes.deleted} deleted since last save`);
  if (saved) summary.push(`base version ${saved}`);
  if (pageCount > 0) summary.push(plural(pageCount, "page"));
  return /* @__PURE__ */ jsxs6(Panel, { "data-testid": "box-rail", children: [
    /* @__PURE__ */ jsxs6(PanelSection, { "data-testid": "selected-panel", children: [
      /* @__PURE__ */ jsxs6(PanelTitleRow, { children: [
        /* @__PURE__ */ jsx8(PanelTitle, { children: selected ? "Selected redaction" : "Redactions" }),
        boxes.length > 0 && /* @__PURE__ */ jsxs6(RailCount, { "data-testid": "rail-count", children: [
          boxes.length,
          " \xB7 ",
          plural(new Set(boxes.map((box) => box.page)).size, "page")
        ] })
      ] }),
      selected ? /* @__PURE__ */ jsxs6(Fragment4, { children: [
        /* @__PURE__ */ jsxs6(KeyValueRow, { children: [
          /* @__PURE__ */ jsx8("span", { children: "Page" }),
          /* @__PURE__ */ jsxs6("span", { children: [
            selected.page + 1,
            " of ",
            pageCount
          ] })
        ] }),
        /* @__PURE__ */ jsxs6(KeyValueRow, { children: [
          /* @__PURE__ */ jsx8("span", { children: "Source" }),
          /* @__PURE__ */ jsx8("span", { "data-testid": "selected-source", children: selected.origin === "manual" ? "Manual" : "AI suggestion" })
        ] }),
        /* @__PURE__ */ jsxs6(KeyValueRow, { children: [
          /* @__PURE__ */ jsx8("span", { children: "Position" }),
          /* @__PURE__ */ jsxs6("span", { "data-testid": "selected-position", children: [
            "x ",
            Math.round(selected.x),
            " \xB7 y ",
            Math.round(selected.y),
            " \xB7 ",
            Math.round(selected.w),
            " \xD7 ",
            Math.round(selected.h),
            " pt"
          ] })
        ] }),
        /* @__PURE__ */ jsx8(FieldLabel, { htmlFor: "redaction-reason", children: "Redaction Reason" }),
        /* @__PURE__ */ jsx8(
          ReasonTextarea,
          {
            id: "redaction-reason",
            value: selected.reason,
            maxLength: maxReasonLength,
            disabled: !editable,
            onChange: (e) => onReason(selected.id, e.target.value),
            onBlur: onReasonBlur,
            placeholder: "e.g. Personal information, FIPPA s. 22(1)",
            "data-testid": "reason-input"
          },
          selected.id
        ),
        /* @__PURE__ */ jsx8(Helper, { children: "Printed beside this box in the reasons PDF when redactions are applied." }),
        /* @__PURE__ */ jsx8("div", { children: /* @__PURE__ */ jsxs6(DangerOutlineButton, { type: "button", onClick: () => onDelete(selected.id), disabled: !editable, "data-testid": "delete-box", children: [
          /* @__PURE__ */ jsx8(DeleteOutlineIcon, {}),
          " Delete box"
        ] }) })
      ] }) : /* @__PURE__ */ jsxs6(EmptyCard, { "data-testid": "empty-selection", children: [
        /* @__PURE__ */ jsx8(TouchAppOutlinedIcon, {}),
        /* @__PURE__ */ jsx8("strong", { children: "Select a box to edit its reason" }),
        /* @__PURE__ */ jsx8(Helper, { children: "Draw on an empty area to add a box. Click or drag an existing box to move it, or pull a handle to resize it. Nothing changes in the file until you Save." })
      ] })
    ] }),
    /* @__PURE__ */ jsx8(RailDivider, {}),
    /* @__PURE__ */ jsxs6(PanelSection, { children: [
      /* @__PURE__ */ jsxs6(RailSectionTitle, { children: [
        "On this page (",
        onThisPage.length,
        ")"
      ] }),
      /* @__PURE__ */ jsx8("div", { "data-testid": "rail-page", "data-page-number": currentPage + 1, children: /* @__PURE__ */ jsxs6(BoxList, { "data-testid": "page-box-list", children: [
        onThisPage.map((box) => /* @__PURE__ */ jsx8(BoxEntry, { box, selectedId, onSelect, colors: tokens.colors }, box.id)),
        !onThisPage.length && /* @__PURE__ */ jsx8(RailEmpty, { children: boxes.length ? "No redactions on this page." : "No boxes yet. Draw on the page to add one." })
      ] }) }),
      otherPages.map((page) => {
        const pageRows = rows.filter((box) => box.page === page);
        return /* @__PURE__ */ jsxs6("div", { "data-testid": "rail-page", "data-page-number": page + 1, children: [
          /* @__PURE__ */ jsxs6(PageGroup, { children: [
            /* @__PURE__ */ jsxs6("span", { children: [
              "Page ",
              page + 1
            ] }),
            /* @__PURE__ */ jsx8("span", { children: pageRows.length })
          ] }),
          /* @__PURE__ */ jsx8(BoxList, { children: pageRows.map((box) => /* @__PURE__ */ jsx8(BoxEntry, { box, selectedId, onSelect, colors: tokens.colors }, box.id)) })
        ] }, page);
      })
    ] }),
    /* @__PURE__ */ jsxs6(FileSummary, { "data-testid": "file-summary", children: [
      /* @__PURE__ */ jsx8("strong", { children: "This file" }),
      /* @__PURE__ */ jsx8("span", { children: summary.join(" \xB7 ") })
    ] })
  ] });
}

// src/OccurrencesDialog.js
import { useRef as useRef7, useState as useState8 } from "react";
import Checkbox from "@mui/material/Checkbox";
import FindReplaceOutlinedIcon from "@mui/icons-material/FindReplaceOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import { Fragment as Fragment5, jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
function OccurrencesDialog({ open, action = "redact", initialText = "", initialReason = "", busy = false, error = null, limits = DEFAULT_LIMITS, onCancel, onConfirm }) {
  const { min: MIN_TEXT, max: MAX_TEXT } = limits.searchText;
  const [text, setText] = useState8(initialText);
  const [reason, setReason] = useState8(initialReason);
  const [wholeWord, setWholeWord] = useState8(true);
  const [caseSensitive, setCaseSensitive] = useState8(false);
  const [submitted, setSubmitted] = useState8(false);
  const textRef = useRef7(null);
  const reasonRef = useRef7(null);
  const opening = open ? [action, initialText, initialReason].join("\0") : null;
  const [shown, setShown] = useState8(opening);
  if (opening !== shown) {
    setShown(opening);
    if (opening !== null) {
      setText(initialText);
      setReason(initialReason);
      setWholeWord(true);
      setCaseSensitive(false);
      setSubmitted(false);
    }
  }
  const focusField = () => (action === "redact" && initialText ? reasonRef : textRef).current?.focus();
  const trimmed = text.trim();
  const textError = trimmed.length < MIN_TEXT ? `Enter at least ${MIN_TEXT} characters to search for.` : trimmed.length > MAX_TEXT ? `Keep the text under ${MAX_TEXT} characters.` : null;
  const reasonError = action === "redact" && !reason.trim() ? "A reason is required. It is printed in the reasons PDF." : null;
  const unredact = action === "unredact";
  function submit(e) {
    e?.preventDefault?.();
    setSubmitted(true);
    if (textError) return textRef.current?.focus();
    if (reasonError) return reasonRef.current?.focus();
    if (busy) return;
    onConfirm({ action, text: trimmed, ...unredact ? {} : { reason: reason.trim() }, caseSensitive, wholeWord });
  }
  return /* @__PURE__ */ jsx9(Dialog, { open, onClose: busy ? void 0 : onCancel, "aria-labelledby": "occurrences-title", TransitionProps: { onEntered: focusField }, children: /* @__PURE__ */ jsxs7("form", { onSubmit: submit, noValidate: true, "data-testid": "occurrences-dialog", children: [
    /* @__PURE__ */ jsx9(DialogTitle, { id: "occurrences-title", children: unredact ? "Un-redact all occurrences" : "Redact all occurrences" }),
    /* @__PURE__ */ jsx9(DialogBody, { style: { marginBottom: 14 }, children: unredact ? "Finds every occurrence in the text layer and by OCR on scanned pages, then removes the boxes sitting on them. The file is saved immediately." : "Finds every occurrence in the text layer and by OCR on scanned pages, then adds a box with the reason below where none exists. Existing boxes are kept. The file is saved immediately." }),
    /* @__PURE__ */ jsxs7(FieldLabel, { htmlFor: "occurrences-text", children: [
      "Text to ",
      unredact ? "un-redact" : "redact",
      /* @__PURE__ */ jsx9(Required, { "aria-hidden": "true", children: "*" })
    ] }),
    /* @__PURE__ */ jsx9(TextInput, { ref: textRef, id: "occurrences-text", value: text, onChange: (e) => setText(e.target.value), maxLength: MAX_TEXT + 20, disabled: busy, "aria-invalid": submitted && Boolean(textError), "data-testid": "occurrences-text" }),
    submitted && textError && /* @__PURE__ */ jsx9(ErrorNote, { role: "alert", children: textError }),
    /* @__PURE__ */ jsxs7(OptionRow, { children: [
      /* @__PURE__ */ jsxs7(ChoiceRow, { children: [
        /* @__PURE__ */ jsx9(Checkbox, { size: "small", checked: wholeWord, onChange: (e) => setWholeWord(e.target.checked), disabled: busy, inputProps: { "data-testid": "occurrences-whole-word" } }),
        /* @__PURE__ */ jsx9(ChoiceText, { children: "Match whole words only" })
      ] }),
      /* @__PURE__ */ jsxs7(ChoiceRow, { children: [
        /* @__PURE__ */ jsx9(Checkbox, { size: "small", checked: caseSensitive, onChange: (e) => setCaseSensitive(e.target.checked), disabled: busy, inputProps: { "data-testid": "occurrences-match-case" } }),
        /* @__PURE__ */ jsx9(ChoiceText, { children: "Match case" })
      ] })
    ] }),
    unredact ? /* @__PURE__ */ jsx9(Helper, { style: { marginTop: 4 }, children: "Only whole boxes sitting on a match are removed; a larger box that also covers other text is kept. Nothing is written to the reasons PDF." }) : /* @__PURE__ */ jsxs7(Fragment5, { children: [
      /* @__PURE__ */ jsxs7(FieldLabel, { htmlFor: "occurrences-reason", children: [
        "Redaction Reason",
        /* @__PURE__ */ jsx9(Required, { "aria-hidden": "true", children: "*" })
      ] }),
      /* @__PURE__ */ jsx9(TextInput, { ref: reasonRef, id: "occurrences-reason", value: reason, onChange: (e) => setReason(e.target.value), maxLength: limits.maxReasonLength, disabled: busy, placeholder: "e.g. Third-party personal information, s. 22(1)", "aria-invalid": submitted && Boolean(reasonError), "data-testid": "occurrences-reason" }),
      submitted && reasonError && /* @__PURE__ */ jsx9(ErrorNote, { role: "alert", children: reasonError })
    ] }),
    error && /* @__PURE__ */ jsx9(ErrorNote, { role: "alert", "data-testid": "occurrences-error", children: error }),
    /* @__PURE__ */ jsxs7(DialogActions, { children: [
      /* @__PURE__ */ jsx9(SecondaryButton, { type: "button", onClick: onCancel, disabled: busy, "data-testid": "occurrences-cancel", children: "Cancel" }),
      /* @__PURE__ */ jsxs7(PrimaryButton, { type: "submit", disabled: busy, "data-testid": "occurrences-confirm", children: [
        busy ? /* @__PURE__ */ jsx9(CircularProgress, { size: 14, sx: { color: "inherit" } }) : unredact ? /* @__PURE__ */ jsx9(RemoveCircleOutlineIcon, {}) : /* @__PURE__ */ jsx9(FindReplaceOutlinedIcon, {}),
        " ",
        busy ? "Finding\u2026" : `Find & ${unredact ? "un-redact" : "redact"}`
      ] })
    ] })
  ] }) });
}

// src/occurrencesSummary.js
var plural2 = (n, word) => `${n} ${n === 1 ? word : word === "box" ? "boxes" : `${word}s`}`;
function occurrencesSummaryParts({ action, text, added = 0, removed = 0, ocrSkippedPages = 0, truncated = false, maxBoxes = 5e3 }) {
  const quoted = `\u201C${text}\u201D`;
  const lead = action === "unredact" ? removed ? `Removed ${plural2(removed, "box")} over ${quoted}.` : `No boxes sit over ${quoted}.` : added ? `Added ${plural2(added, "box")} for ${quoted}.` : `No new occurrences of ${quoted}. Every one found already has a box.`;
  const notes = [];
  if (ocrSkippedPages) notes.push(`${plural2(ocrSkippedPages, "scanned page")} could not be fully searched. Occurrences there may have been missed.`);
  if (truncated) notes.push(`Stopped at the ${maxBoxes}-box cap; some occurrences were left without a box.`);
  return { lead, notes };
}
function occurrencesSummary(input) {
  const { lead, notes } = occurrencesSummaryParts(input);
  return [lead, ...notes].join(" ");
}

// src/RedactionWorkbench.js
import { Fragment as Fragment6, jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var isTyping = (target) => ["INPUT", "TEXTAREA"].includes(target?.tagName) || target?.isContentEditable;
var timeOf2 = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
var plural3 = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
var railOrder = (boxes) => [...boxes].sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x);
function Editor({ ports, pdfjs, idFactory, limits: hostLimits, title, badge, meta, onBack, backLabel = "Back to project", onDirtyChange, onSaved, headerActions, capabilities = ports.capabilities ?? {} }, ref) {
  const { tokens } = useTheme3();
  const limits = useMemo2(() => withDefaultLimits(hostLimits), [hostLimits]);
  const rootRef = useRef8(null);
  const newId = useMemo2(() => idFactory ?? createIdFactory(), [idFactory]);
  const { loadDocument, loadBoxes, save: saveBoxes, watch, findOccurrences } = ports;
  const pdf = usePdfDocument({ pdfjs, loadDocument });
  const { pages } = pdf;
  const pageCount = pages.length;
  const [boxes, setBoxes] = useState9([]);
  const [savedBoxes, setSavedBoxes] = useState9([]);
  const [version, setVersion] = useState9(null);
  const [boxesReady, setBoxesReady] = useState9(false);
  const [loadError, setLoadError] = useState9(null);
  const [selectedId, setSelectedId] = useState9(null);
  const [saving, setSaving] = useState9(false);
  const [saveError, setSaveError] = useState9(null);
  const [stale, setStale] = useState9(null);
  const [occurrences, setOccurrences] = useState9(null);
  const [finding, setFinding] = useState9(false);
  const [occurrencesError, setOccurrencesError] = useState9(null);
  const [band, setBand] = useState9(null);
  const clearSelection = useCallback4(() => setSelectedId(null), []);
  const page = usePageNavigation(pageCount, { onChange: clearSelection });
  const search = useDocumentSearch({ pages, pageNavigation: page, rootRef });
  const pageWidthPoints = pageCount ? Math.max(...pages.map((p) => p.getViewport({ scale: 1 }).width)) : 612;
  const zoom = usePageZoom({ pageWidthPoints, ready: pdf.version > 0 });
  const dirty = useMemo2(() => serializeBoxes(boxes) !== serializeBoxes(savedBoxes), [boxes, savedBoxes]);
  const changes = useMemo2(() => changeSummary(savedBoxes, boxes), [savedBoxes, boxes]);
  const selected = boxes.find((b) => b.id === selectedId) ?? null;
  const busy = saving || !boxesReady;
  const editable = !busy;
  const adopt = useCallback4((rows, nextVersion) => {
    const next = payloadToBoxes(rows, pages, newId);
    setBoxes(next);
    setSavedBoxes(next);
    setVersion(nextVersion);
    setStale(null);
    setSelectedId(null);
  }, [pages, newId]);
  const reload = useCallback4(async () => {
    setSaveError(null);
    try {
      const { boxes: rows, version: v } = await loadBoxes();
      adopt(rows, v);
      setBoxesReady(true);
    } catch (e) {
      setLoadError(e);
    }
  }, [loadBoxes, adopt]);
  useEffect7(() => {
    if (pdf.version > 0) reload();
  }, [pdf.version]);
  useEffect7(() => {
    onDirtyChange?.(dirty, changes);
  }, [dirty, changes]);
  const handleCreate = useCallback4((box) => {
    const created = { ...box, id: newId(), reason: "", origin: "manual" };
    setBoxes((prev) => [...prev, created]);
    setSelectedId(created.id);
  }, [newId]);
  const handleUpdate = useCallback4((id, patch) => setBoxes((prev) => prev.map((b) => b.id === id ? { ...b, ...patch } : b)), []);
  const handleReason = (id, reason) => setBoxes((prev) => prev.map((b) => b.id === id ? { ...b, reason } : b));
  function handleDelete(id) {
    if (!id) return;
    setBoxes((prev) => prev.filter((b) => b.id !== id));
    setSelectedId(null);
  }
  function discard() {
    setBoxes(savedBoxes);
    setSelectedId(null);
    setSaveError(null);
    setStale(null);
  }
  function select(id) {
    setSelectedId(id);
    const box = boxes.find((b) => b.id === id);
    if (box && box.page !== page.pageIndex) page.goToPage(box.page + 1);
  }
  async function save() {
    if (!dirty) return true;
    if (busy || stale) return false;
    setSaving(true);
    setSaveError(null);
    try {
      let res = await saveBoxes(boxesToPayload(boxes, pages), version);
      if (res?.pending) {
        const status = await watch(res.pending);
        if (status?.status !== "DONE") throw new Error(status?.error || "the save did not finish");
        res = { done: status.result ?? {} };
      }
      if (res?.stale) {
        setStale({ currentVersion: res.stale.currentVersion });
        return false;
      }
      const done = res?.done ?? {};
      if (done.boxes) {
        const geometry = selected && { page: selected.page, x: selected.x, y: selected.y };
        adopt(done.boxes, done.version ?? version);
        if (geometry) {
          const again = payloadToBoxes(done.boxes, pages, newId).find((b) => b.page === geometry.page && Math.abs(b.x - geometry.x) < 0.5 && Math.abs(b.y - geometry.y) < 0.5);
          if (again) setSelectedId(again.id);
        }
      } else {
        setSavedBoxes(boxes);
        if (done.version) setVersion(done.version);
      }
      onSaved?.(done);
      return true;
    } catch (e) {
      setSaveError(`Saving failed${e?.message ? `: ${e.message}` : ""}. Your edits are still here. Try again.`);
      return false;
    } finally {
      setSaving(false);
    }
  }
  const markStale = useCallback4((currentVersion) => setStale({ currentVersion }), []);
  async function openOccurrences(action = "redact") {
    let text = "";
    if (selected) {
      try {
        const pdfPage = pages[selected.page];
        text = textUnderBox(await pdfPage.getTextContent(), pdfPage.getViewport({ scale: 1 }), selected);
      } catch {
      }
    }
    setOccurrencesError(null);
    setOccurrences({ text, reason: selected?.reason ?? "", action });
  }
  async function runOccurrences(input) {
    if (dirty || busy) return;
    setFinding(true);
    setOccurrencesError(null);
    setBand(null);
    try {
      let res = await findOccurrences(input, version);
      if (res?.pending) {
        const status = await watch(res.pending);
        if (status?.status !== "DONE") throw new Error(status?.result?.error || status?.error || "the run did not finish");
        res = { done: status.result ?? {} };
      }
      if (res?.stale) {
        setOccurrences(null);
        markStale(res.stale.currentVersion);
        return;
      }
      const done = res?.done ?? {};
      setOccurrences(null);
      await reload();
      const parts = occurrencesSummaryParts({ action: input.action, text: input.text, added: done.added, removed: done.removed, ocrSkippedPages: done.ocrSkippedPages, truncated: done.truncated, maxBoxes: limits.maxBoxes });
      setBand({ ...parts, tone: parts.notes.length ? "warning" : "info" });
    } catch (e) {
      setOccurrencesError(`${input.action === "unredact" ? "Un-redacting" : "Redacting"} all occurrences failed${e?.message ? `: ${e.message}` : ""}. Try again.`);
    } finally {
      setFinding(false);
    }
  }
  useImperativeHandle(ref, () => ({ save, discard, reload, markStale, isDirty: () => dirty, getChanges: () => changes }), [dirty, changes, boxes, savedBoxes, version, busy, stale, pages]);
  useEffect7(() => {
    const doc = rootRef.current?.ownerDocument;
    if (!doc) return void 0;
    function onKeyDown(e) {
      if (isTyping(e.target)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId && editable) {
        e.preventDefault();
        handleDelete(selectedId);
      } else if (e.key === "Escape") setSelectedId(null);
      else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const order = railOrder(boxes);
        if (!order.length) return;
        e.preventDefault();
        const i = order.findIndex((b) => b.id === selectedId);
        const next = i === -1 ? 0 : (i + (e.key === "ArrowDown" ? 1 : -1) + order.length) % order.length;
        select(order[next].id);
      }
    }
    doc.addEventListener("keydown", onKeyDown);
    return () => doc.removeEventListener("keydown", onKeyDown);
  }, [selectedId, editable, boxes, page.pageIndex]);
  const counts = { ai: boxes.filter((b) => b.origin !== "manual").length, manual: boxes.filter((b) => b.origin === "manual").length };
  const metaLine = [meta, pageCount ? plural3(pageCount, "page") : null, boxesReady ? `${counts.ai} suggested` : null, boxesReady ? `${counts.manual} manual` : null, version ? `saved ${timeOf2(version)}` : null].filter(Boolean).join(" \xB7 ");
  const manual = manualFill(tokens.colors);
  const loading = pdf.loading && pdf.version === 0 || !boxesReady && !loadError && !pdf.error;
  const failed = pdf.error && pdf.version === 0 || loadError;
  return /* @__PURE__ */ jsxs8(Shell, { ref: rootRef, "data-testid": "redaction-editor", "data-dirty": dirty ? "true" : "false", children: [
    /* @__PURE__ */ jsxs8(HeaderBar, { children: [
      /* @__PURE__ */ jsx10(IconOnlyButton, { type: "button", "aria-label": backLabel, title: backLabel, onClick: onBack, "data-testid": "back-to-project", children: /* @__PURE__ */ jsx10(ArrowBackIcon, {}) }),
      /* @__PURE__ */ jsxs8(TitleBlock, { children: [
        /* @__PURE__ */ jsxs8(TitleLine, { children: [
          /* @__PURE__ */ jsx10(Title, { title, "data-testid": "editor-title", children: title }),
          badge
        ] }),
        /* @__PURE__ */ jsx10(Meta, { title: metaLine, "data-testid": "editor-meta", children: metaLine })
      ] }),
      /* @__PURE__ */ jsxs8(HeaderActions, { children: [
        dirty && /* @__PURE__ */ jsx10(UnsavedHint, { $error: Boolean(stale), "data-testid": "unsaved-hint", children: stale ? "Unsaved changes \xB7 a newer version exists" : "Unsaved changes" }),
        /* @__PURE__ */ jsx10(SecondaryButton, { type: "button", onClick: discard, disabled: !dirty || saving, "data-testid": "discard-btn", children: "Discard" }),
        /* @__PURE__ */ jsxs8(PrimaryButton, { type: "button", onClick: save, disabled: !dirty || busy || Boolean(stale), "data-testid": "save-btn", children: [
          saving ? /* @__PURE__ */ jsx10(CircularProgress2, { size: 14, sx: { color: "inherit" } }) : /* @__PURE__ */ jsx10(SaveOutlinedIcon, {}),
          " Save"
        ] }),
        headerActions?.({ dirty, busy: busy || Boolean(stale) || finding, saving, save, reload, version, boxCount: boxes.length })
      ] })
    ] }),
    /* @__PURE__ */ jsx10(Toolbar, { $withNote: Boolean(capabilities.findOccurrences && findOccurrences && dirty), children: /* @__PURE__ */ jsxs8(ToolbarLayout, { children: [
      /* @__PURE__ */ jsx10(ToolbarLeft, { children: /* @__PURE__ */ jsx10(DocumentSearchToolbar, { search }) }),
      /* @__PURE__ */ jsx10(ToolbarCenter, { children: /* @__PURE__ */ jsx10(ZoomPageToolbar, { zoom, page, pageCount }) }),
      /* @__PURE__ */ jsx10(ToolbarRight, { children: capabilities.findOccurrences && findOccurrences && /* @__PURE__ */ jsxs8(Fragment6, { children: [
        /* @__PURE__ */ jsxs8(ToolButton, { type: "button", onClick: () => openOccurrences("redact"), disabled: dirty || busy || finding, title: dirty ? "Save your changes before redacting all occurrences" : "Add a box on every occurrence of a text", "data-testid": "redact-all-occurrences", children: [
          /* @__PURE__ */ jsx10(FindReplaceOutlinedIcon2, {}),
          " Redact all"
        ] }),
        /* @__PURE__ */ jsxs8(ToolButton, { type: "button", onClick: () => openOccurrences("unredact"), disabled: dirty || busy || finding, title: dirty ? "Save your changes before un-redacting all occurrences" : "Remove the boxes over every occurrence of a text", "data-testid": "unredact-all-occurrences", children: [
          /* @__PURE__ */ jsx10(RemoveCircleOutlineIcon2, {}),
          " Un-redact all"
        ] })
      ] }) })
    ] }) }),
    capabilities.findOccurrences && findOccurrences && dirty && /* @__PURE__ */ jsx10(ToolNoteRow, { children: /* @__PURE__ */ jsxs8(ToolHint, { "data-testid": "occurrences-hint", children: [
      /* @__PURE__ */ jsx10(InfoOutlinedIcon, {}),
      " Save or discard your changes first"
    ] }) }),
    stale && /* @__PURE__ */ jsxs8(StaleBanner, { role: "alert", "data-testid": "stale-banner", children: [
      /* @__PURE__ */ jsx10(WarningAmberOutlinedIcon, {}),
      /* @__PURE__ */ jsxs8(BannerText, { children: [
        "This file was saved elsewhere at ",
        timeOf2(stale.currentVersion),
        ". Your changes are based on an older version and can't be saved on top of it."
      ] }),
      /* @__PURE__ */ jsx10(SecondaryButton, { type: "button", onClick: () => setStale(null), "data-testid": "stale-keep", children: "Keep editing" }),
      /* @__PURE__ */ jsxs8(PrimaryButton, { type: "button", onClick: reload, "data-testid": "stale-reload", children: [
        /* @__PURE__ */ jsx10(RefreshIcon, {}),
        " Reload latest (discard my changes)"
      ] })
    ] }),
    band && /* @__PURE__ */ jsxs8(InfoBand, { role: "status", "data-testid": "occurrences-summary", "data-tone": band.tone, $tone: band.tone, children: [
      band.tone === "warning" ? /* @__PURE__ */ jsx10(WarningAmberOutlinedIcon, {}) : /* @__PURE__ */ jsx10(InfoOutlinedIcon, {}),
      /* @__PURE__ */ jsxs8(BannerText, { children: [
        /* @__PURE__ */ jsx10(BandLead, { children: band.lead }),
        band.notes.length > 0 && /* @__PURE__ */ jsx10(BandDetail, { children: band.notes.join(" ") })
      ] }),
      /* @__PURE__ */ jsx10(IconOnlyButton, { type: "button", "aria-label": "Dismiss", onClick: () => setBand(null), "data-testid": "occurrences-dismiss", children: /* @__PURE__ */ jsx10(CloseIcon2, {}) })
    ] }),
    saveError && /* @__PURE__ */ jsx10(ErrorLine, { role: "alert", "data-testid": "save-error", children: saveError }),
    failed && /* @__PURE__ */ jsx10(ErrorLine, { role: "alert", "data-testid": "load-error", children: "The file could not be opened. Reload the page to try again." }),
    /* @__PURE__ */ jsxs8(Body, { children: [
      /* @__PURE__ */ jsxs8(CanvasScroll, { ref: zoom.scrollRef, "data-testid": "editor-canvas", children: [
        loading && /* @__PURE__ */ jsx10(CircularProgress2, { size: 28, sx: { mt: 6 }, "data-testid": "editor-loading" }),
        pdf.version > 0 && /* @__PURE__ */ jsx10(
          ContinuousPdfPages,
          {
            pages,
            scale: zoom.scale,
            pageIndex: page.pageIndex,
            navigationKey: page.navigationKey,
            scrollRef: zoom.scrollRef,
            onPageChange: page.setVisiblePage,
            numbered: true,
            renderOverlay: (pageIndex, dims) => /* @__PURE__ */ jsxs8(Fragment6, { children: [
              /* @__PURE__ */ jsx10(RedactionLayer, { pageIndex, dims, boxes, tool: "add", editable, selectedId, onSelect: setSelectedId, onCreate: handleCreate, onUpdate: handleUpdate }),
              /* @__PURE__ */ jsx10(DocumentSearchHighlights, { matches: search.results.map((r, index) => ({ ...r, index })).filter((r) => r.page === pageIndex), activeIndex: search.activeIndex, scale: dims.scale })
            ] })
          }
        ),
        pdf.version > 0 && /* @__PURE__ */ jsxs8(Legend, { "data-testid": "legend", children: [
          /* @__PURE__ */ jsxs8("span", { children: [
            /* @__PURE__ */ jsx10(Swatch, { $fill: aiFill(tokens.colors), $stroke: aiStroke(tokens.colors) }),
            " Suggested (AI)"
          ] }),
          /* @__PURE__ */ jsxs8("span", { children: [
            /* @__PURE__ */ jsx10(Swatch, { $fill: manual, $stroke: tokens.colors.primary }),
            " Manual"
          ] }),
          /* @__PURE__ */ jsxs8("span", { children: [
            /* @__PURE__ */ jsx10(Swatch, { $fill: manual, $stroke: tokens.colors.primary, $selected: true }),
            " Selected"
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx10(
        OccurrencesDialog,
        {
          open: Boolean(occurrences),
          action: occurrences?.action ?? "redact",
          initialText: occurrences?.text ?? "",
          initialReason: occurrences?.reason ?? "",
          busy: finding,
          error: occurrencesError,
          limits,
          onCancel: () => {
            if (!finding) setOccurrences(null);
          },
          onConfirm: runOccurrences
        }
      ),
      /* @__PURE__ */ jsx10(
        SelectedBoxPanel,
        {
          selected,
          boxes,
          currentPage: page.pageIndex,
          selectedId,
          pageCount,
          editable,
          maxReasonLength: limits.maxReasonLength,
          onReason: handleReason,
          onReasonBlur: () => {
          },
          onDelete: handleDelete,
          counts,
          version,
          dirty,
          changes,
          onSelect: select
        }
      )
    ] })
  ] });
}
var EditorWithRef = forwardRef(Editor);
var RedactionWorkbench = forwardRef(function RedactionWorkbench2({ tokens, ...props }, ref) {
  return /* @__PURE__ */ jsx10(WorkbenchTheme, { tokens, children: /* @__PURE__ */ jsx10(EditorWithRef, { ref, ...props }) });
});

// src/RedactionViewer.js
import { useEffect as useEffect8, useRef as useRef9, useState as useState10 } from "react";
import ArrowBackIcon2 from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon2 from "@mui/icons-material/InfoOutlined";
import CircularProgress3 from "@mui/material/CircularProgress";
import { Fragment as Fragment7, jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var THUMB_WIDTH_PX = 44;
var plural4 = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
function Viewer({ ports, pdfjs, title, badge, meta, info, onBack, backLabel = "Back to project", headerActions }) {
  const pdf = usePdfDocument({ pdfjs, loadDocument: ports.loadDocument });
  const { pages } = pdf;
  const pageCount = pages.length;
  const page = usePageNavigation(pageCount);
  const pageWidthPoints = pageCount ? Math.max(...pages.map((p) => p.getViewport({ scale: 1 }).width)) : 612;
  const zoom = usePageZoom({ pageWidthPoints, ready: pdf.version > 0 });
  const [railElement, setRailElement] = useState10(null);
  const activeTileRef = useRef9(null);
  useEffect8(() => {
    activeTileRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [page.pageIndex]);
  const metaLine = [meta, pageCount ? plural4(pageCount, "page") : null].filter(Boolean).join(" \xB7 ");
  return /* @__PURE__ */ jsxs9(Shell, { "data-testid": "redaction-viewer", children: [
    /* @__PURE__ */ jsxs9(HeaderBar, { children: [
      /* @__PURE__ */ jsx11(IconOnlyButton, { type: "button", "aria-label": backLabel, title: backLabel, onClick: onBack, "data-testid": "back-to-project", children: /* @__PURE__ */ jsx11(ArrowBackIcon2, {}) }),
      /* @__PURE__ */ jsxs9(TitleBlock, { children: [
        /* @__PURE__ */ jsxs9(TitleLine, { children: [
          /* @__PURE__ */ jsx11(Title, { title, "data-testid": "editor-title", children: title }),
          badge
        ] }),
        /* @__PURE__ */ jsx11(Meta, { title: metaLine, "data-testid": "editor-meta", children: metaLine })
      ] }),
      /* @__PURE__ */ jsxs9(ViewOnlyPill, { "data-testid": "view-only-pill", children: [
        /* @__PURE__ */ jsx11(LockOutlinedIcon, {}),
        " View only"
      ] }),
      /* @__PURE__ */ jsx11(HeaderActions, { children: headerActions?.() })
    ] }),
    /* @__PURE__ */ jsxs9(Toolbar, { children: [
      /* @__PURE__ */ jsx11(ZoomPageToolbar, { zoom, page, pageCount }),
      info && /* @__PURE__ */ jsxs9(Fragment7, { children: [
        /* @__PURE__ */ jsx11(Sep, {}),
        /* @__PURE__ */ jsxs9(ToolText, { "data-testid": "viewer-info", style: { display: "inline-flex", alignItems: "center", gap: 6 }, children: [
          /* @__PURE__ */ jsx11(InfoOutlinedIcon2, {}),
          " ",
          info
        ] })
      ] }),
      /* @__PURE__ */ jsx11(Spacer, {})
    ] }),
    pdf.error && pdf.version === 0 && /* @__PURE__ */ jsx11(ErrorLine, { role: "alert", "data-testid": "load-error", children: "The file could not be opened. Reload the page to try again." }),
    /* @__PURE__ */ jsxs9(Body, { children: [
      /* @__PURE__ */ jsxs9(CanvasScroll, { ref: zoom.scrollRef, "data-testid": "viewer-canvas", children: [
        pdf.loading && pdf.version === 0 && /* @__PURE__ */ jsx11(CircularProgress3, { size: 28, sx: { mt: 6 }, "data-testid": "editor-loading" }),
        pdf.version > 0 && /* @__PURE__ */ jsx11(ContinuousPdfPages, { pages, scale: zoom.scale, pageIndex: page.pageIndex, navigationKey: page.navigationKey, scrollRef: zoom.scrollRef, onPageChange: page.setVisiblePage, paintAnnotations: true, numbered: true })
      ] }),
      /* @__PURE__ */ jsxs9(Panel, { ref: setRailElement, "data-testid": "page-rail", children: [
        /* @__PURE__ */ jsx11(PanelTitle, { children: "Pages" }),
        /* @__PURE__ */ jsx11(Helper, { children: "Jump to a page here or from the page box in the toolbar." }),
        /* @__PURE__ */ jsx11(PageTileList, { children: pages.map((railPage, index) => /* @__PURE__ */ jsxs9(
          PageTile,
          {
            type: "button",
            ref: index === page.pageIndex ? activeTileRef : void 0,
            $active: index === page.pageIndex,
            "aria-current": index === page.pageIndex ? "page" : void 0,
            onClick: () => page.goToPage(index + 1),
            "data-testid": "page-tile",
            children: [
              /* @__PURE__ */ jsx11(Thumb, { "aria-hidden": "true", children: /* @__PURE__ */ jsx11(LazyPdfPage, { page: railPage, pageIndex: index, scale: THUMB_WIDTH_PX / railPage.getViewport({ scale: 1 }).width, active: index === page.pageIndex, scrollRoot: railElement, paintAnnotations: true, testId: "pdf-thumb", placeholderTestId: "pdf-thumb-placeholder" }) }),
              "Page ",
              index + 1
            ]
          },
          index
        )) })
      ] })
    ] })
  ] });
}
function RedactionViewer({ tokens, ...props }) {
  return /* @__PURE__ */ jsx11(WorkbenchTheme, { tokens, children: /* @__PURE__ */ jsx11(Viewer, { ...props }) });
}

// src/UnsavedChangesDialog.js
import SaveOutlinedIcon2 from "@mui/icons-material/SaveOutlined";
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var plural5 = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
function describeChanges(changes) {
  changes = changes ?? {};
  const parts = [];
  if (changes.added) parts.push(`${plural5(changes.added, "box")} added`);
  if (changes.moved) parts.push(`${changes.moved} moved`);
  if (changes.reworded) parts.push(`${changes.reworded} reason${changes.reworded === 1 ? "" : "s"} edited`);
  if (changes.deleted) parts.push(`${changes.deleted} deleted`);
  return parts.join(", ");
}
function UnsavedChangesDialog({ open, tokens, fileName, changes, saving = false, destination = "the project", onKeepEditing, onDiscard, onSaveAndLeave }) {
  const total = Math.max(changes?.total ?? 0, 1);
  const detail = describeChanges(changes);
  return /* @__PURE__ */ jsx12(WorkbenchTheme, { tokens, children: /* @__PURE__ */ jsxs10(Dialog, { open, onClose: onKeepEditing, "aria-labelledby": "unsaved-changes-title", "data-testid": "unsaved-dialog", children: [
    /* @__PURE__ */ jsx12(DialogTitle, { id: "unsaved-changes-title", children: "Leave without saving?" }),
    /* @__PURE__ */ jsxs10(DialogBody, { children: [
      "You have ",
      plural5(total, "unsaved change"),
      " to \u201C",
      fileName,
      "\u201D",
      detail ? ` (${detail})` : "",
      ". Save ",
      total === 1 ? "it" : "them",
      ", or discard and go back to ",
      destination,
      "."
    ] }),
    /* @__PURE__ */ jsxs10(DialogActions, { children: [
      /* @__PURE__ */ jsx12(TextButton, { $danger: true, onClick: onDiscard, disabled: saving, "data-testid": "guard-discard", children: "Discard changes" }),
      /* @__PURE__ */ jsx12(SecondaryButton, { onClick: onKeepEditing, disabled: saving, "data-testid": "guard-keep", children: "Keep editing" }),
      /* @__PURE__ */ jsxs10(PrimaryButton, { onClick: onSaveAndLeave, disabled: saving, "data-testid": "guard-save", children: [
        /* @__PURE__ */ jsx12(SaveOutlinedIcon2, {}),
        " ",
        saving ? "Saving\u2026" : "Save and leave"
      ] })
    ] })
  ] }) });
}
export {
  DEFAULT_LIMITS,
  HEADER_HEIGHT,
  PdfPageCanvas,
  RedactionViewer,
  RedactionWorkbench,
  TOOLBAR_HEIGHT,
  UnsavedChangesDialog,
  WorkbenchTheme,
  ZOOM_STEPS,
  boxToUnrotatedRect,
  boxesToPayload,
  changeSummary,
  createIdFactory,
  describeChanges,
  nextZoom,
  occurrencesSummary,
  occurrencesSummaryParts,
  payloadToBoxes,
  serializeBoxes,
  unrotatedRectToBox,
  usePdfDocument
};
